// Data-aware lookback helpers shared by every portfolio tool that reads the
// local `diversification_daily_prices` store (Drift Auditor, Drift Auditor PRO,
// Diversification Checker, Diversification Optimizer PRO).
//
// Mirrors the reference `buildLookbackOptions` in
// features/portfolio-drift-auditor/components/DriftAuditorForm.tsx: drop year
// options the store cannot satisfy for the chosen tickers and, when the real
// overlap is not represented by a standard year, append an explicit
// "max available" option so the user can always run the full window.

// Minimal structural slice of every domain's CoverageResponse DTO.
export interface LookbackCoverage {
    max_lookback_years: number;
}

export interface LookbackOption {
    value: number;
    label: string;
    // Compact label for button-group selectors (e.g. "6.3Y max").
    shortLabel: string;
    isMaxAvailable: boolean;
}

const yearLabel = (y: number): string => `${y} ${y === 1 ? 'Year' : 'Years'}`;

// "~6.3 years" / "~7 months" -- human-readable span of shared history.
export const formatHistorySpan = (years: number): string =>
    years >= 1
        ? `~${years.toFixed(1)} years`
        : `~${Math.max(1, Math.round(years * 12))} months`;

export const buildLookbackOptions = (
    standardOptions: readonly number[],
    coverage?: LookbackCoverage | null,
): LookbackOption[] => {
    const toStd = (y: number): LookbackOption => ({
        value: y,
        label: yearLabel(y),
        shortLabel: yearLabel(y),
        isMaxAvailable: false,
    });
    const maxY = coverage?.max_lookback_years ?? 0;

    // No coverage info yet (loading / tickers invalid) -> full standard list
    // rather than blocking the UI.
    if (!coverage || maxY <= 0) return standardOptions.map(toStd);

    const opts = standardOptions.filter((y) => y <= maxY).map(toStd);
    const topStd = opts.length ? opts[opts.length - 1].value : 0;
    const maxStd = standardOptions.length ? standardOptions[standardOptions.length - 1] : 0;
    const value = Math.max(1, Math.ceil(maxY));

    // Explicit "max available" option when the real window isn't already a
    // standard year (e.g. ~6 months, or 6.3y between the 5y/10y tiers). Never
    // exceed the domain's largest standard tier (= the API's lookback bound).
    if ((opts.length === 0 || maxY - topStd >= 0.5) && value <= maxStd && !opts.some((o) => o.value === value)) {
        const months = Math.max(1, Math.round(maxY * 12));
        opts.push({
            value,
            label: maxY >= 1 ? `${maxY.toFixed(1)} Years (max available)` : `~${months} Months (max available)`,
            shortLabel: maxY >= 1 ? `${maxY.toFixed(1)}Y max` : `~${months}M max`,
            isMaxAvailable: true,
        });
    }
    return opts;
};

// Upper-case, regex-validated, de-duplicated tickers suitable for a coverage probe.
export const toCoverageTickers = (raw: readonly string[], tickerRe: RegExp): string[] => {
    const out: string[] = [];
    const seen = new Set<string>();
    for (const r of raw) {
        const t = r.trim().toUpperCase();
        if (t && tickerRe.test(t) && !seen.has(t)) {
            seen.add(t);
            out.push(t);
        }
    }
    return out;
};
